import React, { useState, useRef, useCallback, useEffect } from 'react';
import { ReactFlow, ReactFlowProvider, addEdge, useNodesState, useEdgesState, Controls, Background, BackgroundVariant, MarkerType } from '@xyflow/react';
import '@xyflow/react/dist/style.css';
import EventNode from './nodes/EventNode';
import ConditionNode from './nodes/ConditionNode';
import ActionNode from './nodes/ActionNode';
import Icon from '../../ui/Icon';
import { T } from '../../constants/mockData';
import { workflowsAPI, groupsAPI } from '../../services/api';
import { formatInTz } from '../../utils/timezone';

const WORKSPACE_GROUP_NAMES = ['Action Taker 1', 'Action Taker 2', 'Action Taker 3'];

// ── Pre-built workflow templates ──────────────────────────────────────────────
const TEMPLATES = [
  {
    name: '⚡ Auto-Assign All New Tickets',
    description: 'Every new ticket is instantly assigned to Action Taker 1. Works immediately on ticket creation.',
    nodes: [
      { id: 'aa_ev',  type: 'Event',  position: { x: 60,  y: 200 }, data: { label: 'Ticket Created', performedBy: 'Anyone', events: [{ type: 'ticket_action', action: 'create' }] } },
      { id: 'aa_act', type: 'Action', position: { x: 420, y: 200 }, data: { label: 'Assign to Action Taker 1', actionData: { actionType: 'assign_group', group: 'Action Taker 1' } } },
    ],
    edges: [
      { id: 'aa_e1', source: 'aa_ev', target: 'aa_act', sourceHandle: 'a', type: 'smoothstep', markerEnd: { type: MarkerType.ArrowClosed, color: '#718096' }, style: { stroke: '#3B5BDB', strokeWidth: 2 } },
    ],
  },
  {
    name: 'Auto-Assign by Priority',
    description: 'When a high-priority ticket is created, auto-assign it to Action Taker 1.',
    nodes: [
      { id: 't1_ev',   type: 'Event',     position: { x: 60,  y: 180 }, data: { label: 'Ticket is raised', performedBy: 'Anyone', events: [{ type: 'ticket_action', action: 'create' }] } },
      { id: 't1_cond', type: 'Condition', position: { x: 360, y: 170 }, data: { label: 'Priority is High or Urgent', conditionData: { matchType: 'Any', conditions: [{ field: 'priority', op: 'is', value: 'High' }, { field: 'priority', op: 'is', value: 'Urgent' }] } } },
      { id: 't1_act1', type: 'Action',    position: { x: 680, y: 80  }, data: { label: 'Assign to Action Taker 1', actionData: { actionType: 'assign_group', group: 'Action Taker 1' } } },
      { id: 't1_act2', type: 'Action',    position: { x: 680, y: 300 }, data: { label: 'Change Priority to Medium',       actionData: { actionType: 'change_priority', priority: 'Medium' } } },
    ],
    edges: [
      { id: 'te1', source: 't1_ev',   target: 't1_cond', sourceHandle: 'a',   type: 'smoothstep', markerEnd: { type: MarkerType.ArrowClosed, color: '#718096' }, style: { stroke: '#718096', strokeWidth: 1.5 } },
      { id: 'te2', source: 't1_cond', target: 't1_act1', sourceHandle: 'yes', type: 'smoothstep', label: 'YES', labelStyle: { fontSize: 11, fontWeight: 700, fill: '#3B5BDB' }, labelBgStyle: { fill: 'transparent' }, markerEnd: { type: MarkerType.ArrowClosed, color: '#718096' }, style: { stroke: '#718096', strokeWidth: 1.5 } },
      { id: 'te3', source: 't1_cond', target: 't1_act2', sourceHandle: 'no',  type: 'smoothstep', label: 'NO',  labelStyle: { fontSize: 11, fontWeight: 700, fill: '#ef4444' }, labelBgStyle: { fill: 'transparent' }, markerEnd: { type: MarkerType.ArrowClosed, color: '#718096' }, style: { stroke: '#718096', strokeWidth: 1.5 } },
    ],
  },
  {
    name: 'Auto-Resolve Closed Tickets',
    description: 'When a ticket is updated to Closed status, automatically set it as Resolved.',
    nodes: [
      { id: 't2_ev',  type: 'Event',  position: { x: 60,  y: 200 }, data: { label: 'Ticket is updated', performedBy: 'Anyone', events: [{ type: 'ticket_action', action: 'update' }] } },
      { id: 't2_act', type: 'Action', position: { x: 400, y: 200 }, data: { label: 'Change Status to Resolved', actionData: { actionType: 'change_status', status: 'Resolved' } } },
    ],
    edges: [
      { id: 'te4', source: 't2_ev', target: 't2_act', sourceHandle: 'a', type: 'smoothstep', markerEnd: { type: MarkerType.ArrowClosed, color: '#718096' }, style: { stroke: '#718096', strokeWidth: 1.5 } },
    ],
  },
  {
    name: 'Email Ticket Auto-Route',
    description: 'When a ticket arrives via Email, assign it to Action Taker 1 and notify all agents.',
    nodes: [
      { id: 't3_ev',   type: 'Event',     position: { x: 60,  y: 200 }, data: { label: 'Ticket is raised', performedBy: 'Anyone', events: [{ type: 'ticket_action', action: 'create' }] } },
      { id: 't3_cond', type: 'Condition', position: { x: 360, y: 190 }, data: { label: 'Source is Email', conditionData: { matchType: 'Any', conditions: [{ field: 'source', op: 'is', value: 'Email' }] } } },
      { id: 't3_act1', type: 'Action',    position: { x: 680, y: 100 }, data: { label: 'Assign to Action Taker 1', actionData: { actionType: 'assign_group', group: 'Action Taker 1' } } },
      { id: 't3_act2', type: 'Action',    position: { x: 680, y: 300 }, data: { label: 'Send Notification to Agents',       actionData: { actionType: 'send_notification' } } },
    ],
    edges: [
      { id: 'te5', source: 't3_ev',   target: 't3_cond', sourceHandle: 'a',   type: 'smoothstep', markerEnd: { type: MarkerType.ArrowClosed, color: '#718096' }, style: { stroke: '#718096', strokeWidth: 1.5 } },
      { id: 'te6', source: 't3_cond', target: 't3_act1', sourceHandle: 'yes', type: 'smoothstep', label: 'YES', labelStyle: { fontSize: 11, fontWeight: 700, fill: '#3B5BDB' }, labelBgStyle: { fill: 'transparent' }, markerEnd: { type: MarkerType.ArrowClosed, color: '#718096' }, style: { stroke: '#718096', strokeWidth: 1.5 } },
      { id: 'te7', source: 't3_cond', target: 't3_act2', sourceHandle: 'no',  type: 'smoothstep', label: 'NO',  labelStyle: { fontSize: 11, fontWeight: 700, fill: '#ef4444' }, labelBgStyle: { fill: 'transparent' }, markerEnd: { type: MarkerType.ArrowClosed, color: '#718096' }, style: { stroke: '#718096', strokeWidth: 1.5 } },
    ],
  },
];

const nodeTypes = { Event: EventNode, Condition: ConditionNode, Action: ActionNode };

let dndId = 0;
const getDnDId = () => `dndnode_${dndId++}`;

const MODULES = [
  { name: 'Tickets', icon: 'ticket', sub: ['Event Based Workflows', 'Scheduled Workflows', 'Subflows'] },
  { name: 'Problems', icon: 'alert', sub: [] },
  { name: 'Changes', icon: 'refresh', sub: [] },
  { name: 'Releases', icon: 'layers', sub: [] },
  { name: 'Alerts', icon: 'bell', sub: [] },
  { name: 'Tasks', icon: 'task', sub: [] },
  { name: 'Assets', icon: 'monitor', sub: [] },
  { name: 'Software Users', icon: 'grid', sub: [] },
  { name: 'Trash', icon: 'trash', sub: [] },
];

const CANVAS_NODES = [
  { type: 'Event',      label: 'EVENT',       color: '#3B5BDB', icon: 'check' },
  { type: 'Condition',  label: 'CONDITION',   color: '#f97316', icon: 'filter' },
  { type: 'Action',     label: 'ACTION',      color: '#3b82f6', icon: 'promote' },
  { type: 'Reader',     label: 'READER',      color: '#3B5BDB', icon: 'eye' },
  { type: 'App',        label: 'APP',         color: '#8b5cf6', icon: 'grid' },
  { type: 'JsonParser', label: 'JSON PARSER', color: '#0891b2', icon: 'note' },
  { type: 'Expression', label: 'EXPRESSION',  color: '#f43f5e', icon: 'chart' },
  { type: 'WebRequest', label: 'WEB REQUEST', color: '#22c55e', icon: 'link' },
  { type: 'Timer',      label: 'TIMER',       color: '#f59e0b', icon: 'clock' },
];

const MODULE_OPTS  = ['Tickets', 'Problems', 'Changes', 'Releases'];
const TYPE_OPTS    = ['Event Based Workflow', 'Scheduled Workflow'];

const MEMBER_OPTS = [
  { label: 'Anyone',           value: 'Anyone' },
  { label: 'Requester',        value: 'Requester' },
  { label: 'Agent',            value: 'Agent' },
  { label: 'Any agent except', value: 'Any agent except' },
  { label: 'System',           value: 'System' },
];

const EVENT_TYPE_OPTS = [
  { label: 'Ticket is',          value: 'ticket_action' },
  { label: 'Incident is',        value: 'incident' },
  { label: 'Service Request is', value: 'service_request' },
  { label: 'Major Incident is',  value: 'major_incident' },
];

const TICKET_ACTION_OPTS = [
  { label: 'raised',              value: 'create' },
  { label: 'updated',             value: 'update' },
  { label: 'replied to',          value: 'reply' },
  { label: 'status changed',      value: 'status_changed' },
  { label: 'priority changed',    value: 'priority_changed' },
  { label: 'assigned',            value: 'assigned' },
  { label: 'resolved',            value: 'resolved' },
  { label: 'closed',              value: 'closed' },
  { label: 'reopened',            value: 'reopened' },
  { label: 'deleted',             value: 'delete' },
  { label: 'marked as spam',      value: 'marked_spam' },
];

const CONDITION_FIELD_OPTS = [
  { label: 'From Email',          value: 'from_email' },
  { label: 'Subject',             value: 'subject' },
  { label: 'Description',         value: 'description' },
  { label: 'Priority',            value: 'priority' },
  { label: 'Status',              value: 'status' },
  { label: 'Type',                value: 'type' },
  { label: 'Source',              value: 'source' },
  { label: 'Department',          value: 'department' },
  { label: 'Category',            value: 'category' },
  { label: 'Group',               value: 'group' },
  { label: 'Assigned To',         value: 'assigned_to' },
  { label: 'Requester',           value: 'requester' },
  { label: 'Organization Unit',   value: 'org_unit' },
  { label: 'Head of Department',  value: 'head_of_department' },
  { label: 'Tags',                value: 'tags' },
];

// Smart value options for specific fields
const CONDITION_VALUE_OPTS = {
  priority:  ['Low', 'Medium', 'High', 'Urgent'],
  status:    ['Open', 'Pending Action', 'Pending Approval', 'Pending User Acceptance', 'Completed', 'Rejected', 'Voided'],
  type:      ['Incident', 'Service Request', 'Problem', 'Change'],
  source:    ['Email', 'Portal', 'Phone', 'Chat', 'Walk-in'],
};

const CONDITION_OP_OPTS = [
  { label: 'is',               value: 'is' },
  { label: 'is not',           value: 'is_not' },
  { label: 'contains',         value: 'contains' },
  { label: 'does not contain', value: 'not_contains' },
  { label: 'starts with',      value: 'starts_with' },
  { label: 'ends with',        value: 'ends_with' },
  { label: 'is empty',         value: 'is_empty' },
  { label: 'is not empty',     value: 'is_not_empty' },
];

const ACTION_TYPE_OPTS = [
  { label: 'Assign to Group',              value: 'assign_group' },
  { label: 'Assign to Agent',              value: 'assign_agent' },
  { label: 'Send Email to Requester',      value: 'send_email' },
  { label: 'Send Notification to Agents',  value: 'send_notification' },
  { label: 'Add Note',                     value: 'add_note' },
  { label: 'Change Priority',              value: 'change_priority' },
  { label: 'Change Status',               value: 'change_status' },
  { label: 'Change Department',            value: 'change_department' },
  { label: 'Change Category',              value: 'change_category' },
  { label: 'Set Due Date',                 value: 'set_due_date' },
];

const ACTION_PRIORITY_OPTS = [
  { label: 'Low', value: 'Low' }, { label: 'Medium', value: 'Medium' },
  { label: 'High', value: 'High' }, { label: 'Urgent', value: 'Urgent' },
];

const ACTION_STATUS_OPTS = [
  { label: 'Open', value: 'Open' },
  { label: 'Pending Action', value: 'Pending Action' },
  { label: 'Pending Approval', value: 'Pending Approval' },
  { label: 'Pending User Acceptance', value: 'Pending User Acceptance' },
  { label: 'Completed', value: 'Completed' },
  { label: 'Rejected', value: 'Rejected' },
  { label: 'Voided', value: 'Voided' },
];

const EDGE_DEFAULTS = { type: 'smoothstep', markerEnd: { type: MarkerType.ArrowClosed, color: '#718096' }, style: { stroke: '#718096', strokeWidth: 1.5 } };

const buildDemoFlow = () => {
  const evId = 'demo_ev', condId = 'demo_cond', act1Id = 'demo_act1', act2Id = 'demo_act2';
  return {
    nodes: [
      { id: evId,   type: 'Event',     position: { x: 40,  y: 180 }, data: { label: 'Ticket is raised', performedBy: 'Anyone', events: [{ type: 'ticket_action', action: 'create' }] } },
      { id: condId, type: 'Condition', position: { x: 380, y: 170 }, data: { label: 'Ticket fields.from email is tung.nguyen...' } },
      { id: act1Id, type: 'Action',    position: { x: 680, y: 100 }, data: { label: 'Assign to group first responders group' } },
      { id: act2Id, type: 'Action',    position: { x: 380, y: 420 }, data: { label: 'Assign to group first responders group' } },
    ],
    edges: [
      { id: 'de1', source: evId,   target: condId, sourceHandle: 'a',   ...EDGE_DEFAULTS },
      { id: 'de2', source: condId, target: act1Id, sourceHandle: 'yes', label: 'YES', labelStyle: { fontSize: 11, fontWeight: 700, fill: '#718096' }, labelBgStyle: { fill: 'transparent' }, ...EDGE_DEFAULTS },
      { id: 'de3', source: condId, target: act2Id, sourceHandle: 'no',  label: 'NO',  labelStyle: { fontSize: 11, fontWeight: 700, fill: '#718096' }, labelBgStyle: { fill: 'transparent' }, ...EDGE_DEFAULTS },
    ],
  };
};

const buildNewFlow = () => ({
  nodes: [
    { id: 'new_ev', type: 'Event', position: { x: 200, y: 200 }, data: { label: 'Ticket is raised', performedBy: 'Anyone', events: [{ type: 'ticket_action', action: 'create' }] } },
  ],
  edges: [],
});

export default function WorkflowPage() {
  const [workflows, setWorkflows]       = useState([]);
  const [activeModule, setActiveModule] = useState('Tickets');
  const [activeSubItem, setActiveSubItem] = useState('Event Based Workflows');
  const [wfListTab, setWfListTab]       = useState('workflows'); // 'workflows' | 'logs'
  const [logs, setLogs]                 = useState([]);
  const [logsLoading, setLogsLoading]   = useState(false);
  const [showTemplates, setShowTemplates] = useState(false);
  const [workflowGroups, setWorkflowGroups] = useState([]);

  const [searchWf, setSearchWf]         = useState('');

  const [createOpen, setCreateOpen]     = useState(false);
  const [createTitle, setCreateTitle]   = useState('');
  const [createDesc, setCreateDesc]     = useState('');
  const [showDesc, setShowDesc]         = useState(false);
  const [createModule, setCreateModule] = useState('Tickets');
  const [createType, setCreateType]     = useState('Event Based Workflow');

  const [editingWf, setEditingWf]             = useState(null);
  const [canvasStatus, setCanvasStatus]       = useState('Draft');
  const [showActivateConfirm, setShowActivateConfirm] = useState(false);
  const [toast, setToast]                     = useState(null);

  // Load workflows from API on mount
  useEffect(() => {
    workflowsAPI.list()
      .then(data => {
        setWorkflows((data || []).map(w => ({
          id: w.id,
          title: w.name || w.title || '',
          description: w.description || '',
          activatedBy: w.activated_by_name || w.activated_by || '',
          activatedOn: w.activated_at ? formatInTz(w.activated_at, { day: '2-digit', month: 'short', year: 'numeric' }) : '',
          updatedBy: w.updated_by_name || '',
          updatedOn: w.updated_at ? formatInTz(w.updated_at, { day: '2-digit', month: 'short', year: 'numeric' }) : '',
          status: w.status === 'active' ? 'Active' : w.status === 'draft' ? 'Draft' : w.status || 'Draft',
          module: w.module || 'Tickets',
          workflowType: w.workflow_type || w.workflowType || 'Event Based Workflow',
          _nodes: Array.isArray(w.nodes) ? w.nodes : (w.nodes ? JSON.parse(w.nodes) : []),
          _edges: Array.isArray(w.edges) ? w.edges : (w.edges ? JSON.parse(w.edges) : []),
        })));
      })
      .catch(err => console.error('Failed to load workflows:', err));
  }, []);

  // Load groups from database for workflow actions
  useEffect(() => {
    groupsAPI.list()
      .then(data => {
        const all = Array.isArray(data) ? data : (data.groups || []);
        const filtered = all.filter(g => WORKSPACE_GROUP_NAMES.includes(g.name));
        setWorkflowGroups(filtered.length ? filtered : all);
      })
      .catch(err => console.error('Failed to load groups:', err));
  }, []);

  const resolveGroupName = (saved) => {
    if (!saved) return workflowGroups[0]?.name || '';
    if (workflowGroups.some(g => g.name === saved)) return saved;
    const legacy = {
      first_responders: 'Action Taker 1',
      network_team: 'Action Taker 2',
      it_support: 'Action Taker 3',
      'First Responders Group': 'Action Taker 1',
    }[saved];
    if (legacy && workflowGroups.some(g => g.name === legacy)) return legacy;
    return workflowGroups[0]?.name || saved;
  };

  const [selectedNode, setSelectedNode]         = useState(null);
  const [eventPanelData, setEventPanelData]     = useState({ performedBy: 'Anyone', events: [{ type: 'ticket_action', action: 'create' }] });
  const [conditionPanelData, setConditionPanelData] = useState({ matchType: 'Any', conditions: [{ field: 'from_email', op: 'is', value: '' }] });
  const [actionPanelData, setActionPanelData]   = useState({ actionType: 'assign_group', group: '', agentName: '', note: '', priority: 'high', status: 'resolved' });

  const reactFlowWrapper = useRef(null);
  const [nodes, setNodes, onNodesChange] = useNodesState([]);
  const [edges, setEdges, onEdgesChange] = useEdgesState([]);
  const [rfInstance, setRfInstance]      = useState(null);

  const showToast = msg => { setToast(msg); setTimeout(() => setToast(null), 3000); };

  const onConnect    = useCallback(params => setEdges(eds => addEdge({ ...params, ...EDGE_DEFAULTS }, eds)), [setEdges]);
  const onDragOver   = useCallback(e => { e.preventDefault(); e.dataTransfer.dropEffect = 'move'; }, []);
  const onDrop       = useCallback(e => {
    e.preventDefault();
    const type = e.dataTransfer.getData('application/reactflow');
    if (!type) return;
    const position = rfInstance.screenToFlowPosition({ x: e.clientX, y: e.clientY });
    const labelMap = { Event: 'Ticket is raised', Condition: 'Ticket fields condition', Action: 'Assign to group' };
    const initialData = type === 'Event'
      ? { label: 'Ticket is raised', performedBy: 'Anyone', events: [{ type: 'ticket_action', action: 'create' }] }
      : { label: labelMap[type] || type };
    setNodes(nds => nds.concat({ id: getDnDId(), type, position, data: initialData }));
  }, [rfInstance, setNodes]);

  const onDragStart  = (e, type) => { e.dataTransfer.setData('application/reactflow', type); e.dataTransfer.effectAllowed = 'move'; };

  // Load execution logs when logs tab is opened
  useEffect(() => {
    if (wfListTab === 'logs') {
      setLogsLoading(true);
      workflowsAPI.allLogs(200)
        .then(data => setLogs(data || []))
        .catch(() => setLogs([]))
        .finally(() => setLogsLoading(false));
    }
  }, [wfListTab]);

  const openCanvas = wf => {
    // Load actual saved nodes/edges from the workflow, fallback to demo
    const savedNodes = wf._nodes && wf._nodes.length > 0 ? wf._nodes : null;
    const savedEdges = wf._edges && wf._edges.length > 0 ? wf._edges : null;
    if (savedNodes) {
      setNodes(savedNodes);
      setEdges(savedEdges || []);
    } else {
      const { nodes: n, edges: e } = buildDemoFlow();
      setNodes(n); setEdges(e);
    }
    setCanvasStatus(wf.status); setEditingWf(wf); setSelectedNode(null);
  };

  const loadTemplateWorkflow = async (template) => {
    try {
      const created = await workflowsAPI.create({
        title: template.name,
        description: template.description,
        module: 'Tickets',
        workflow_type: 'Event Based Workflow',
        nodes: template.nodes,
        edges: template.edges,
      });
      const wf = {
        id: created.id,
        title: created.title || template.name,
        description: created.description || template.description,
        activatedBy: '', activatedOn: '', updatedBy: '', updatedOn: '',
        status: 'Draft',
        module: 'Tickets',
        workflowType: 'Event Based Workflow',
        _nodes: template.nodes,
        _edges: template.edges,
      };
      setWorkflows(wfs => [...wfs, wf]);
      setShowTemplates(false);
      setNodes(template.nodes);
      setEdges(template.edges);
      setCanvasStatus('Draft');
      setEditingWf(wf);
      showToast(`Template "${template.name}" loaded — review and activate!`);
    } catch (err) {
      showToast('Failed to load template: ' + err.message);
    }
  };

  // Create + immediately activate in one click
  const deployTemplate = async (template) => {
    try {
      const created = await workflowsAPI.create({
        title: template.name,
        description: template.description,
        module: 'Tickets',
        workflow_type: 'Event Based Workflow',
        nodes: template.nodes,
        edges: template.edges,
      });
      await workflowsAPI.update(created.id, { status: 'Active' });
      const wf = {
        id: created.id,
        title: created.title || template.name,
        description: created.description || template.description,
        activatedBy: 'You', activatedOn: formatInTz(new Date(), { day: '2-digit', month: 'short', year: 'numeric' }),
        updatedBy: '', updatedOn: '',
        status: 'Active',
        module: 'Tickets',
        workflowType: 'Event Based Workflow',
        _nodes: template.nodes,
        _edges: template.edges,
      };
      setWorkflows(wfs => [...wfs, wf]);
      setShowTemplates(false);
      showToast(`✅ "${template.name}" is now ACTIVE — new tickets will be auto-assigned!`);
    } catch (err) {
      showToast('Failed to deploy: ' + err.message);
    }
  };

  const openNodePanel = node => {
    setSelectedNode(node);
    if (node.type === 'Event') {
      setEventPanelData({ performedBy: node.data.performedBy || 'Anyone', events: node.data.events || [{ type: 'ticket_action', action: 'create' }] });
    } else if (node.type === 'Condition') {
      setConditionPanelData(node.data.conditionData || { matchType: 'Any', conditions: [{ field: 'from_email', op: 'is', value: '' }] });
    } else if (node.type === 'Action') {
      const ad = node.data.actionData || { actionType: 'assign_group', group: '', agentName: '', note: '', priority: 'Medium', status: 'Open' };
      setActionPanelData({ ...ad, group: resolveGroupName(ad.group) });
    }
  };

  const handleEventPanelDone = () => {
    const summary = eventPanelData.events.map(ev => {
      const t = EVENT_TYPE_OPTS.find(o => o.value === ev.type)?.label || 'Ticket is';
      const a = TICKET_ACTION_OPTS.find(o => o.value === ev.action)?.label || 'raised';
      return `${t} ${a}`;
    }).join(', ');
    setNodes(nds => nds.map(n => n.id === selectedNode.id
      ? { ...n, data: { ...n.data, label: summary, performedBy: eventPanelData.performedBy, events: eventPanelData.events } }
      : n
    ));
    setSelectedNode(null);
  };

  const updateEventRow = (i, field, value) =>
    setEventPanelData(d => ({ ...d, events: d.events.map((ev, idx) => idx === i ? { ...ev, [field]: value } : ev) }));

  const removeEventRow = i =>
    setEventPanelData(d => ({ ...d, events: d.events.filter((_, idx) => idx !== i) }));

  const addEventRow = () =>
    setEventPanelData(d => ({ ...d, events: [...d.events, { type: 'ticket_action', action: 'create' }] }));

  const handleConditionDone = () => {
    const summary = conditionPanelData.conditions.map(c => {
      const f = CONDITION_FIELD_OPTS.find(o => o.value === c.field)?.label || c.field;
      const op = CONDITION_OP_OPTS.find(o => o.value === c.op)?.label || c.op;
      return `${f} ${op}${c.value ? ' ' + c.value : ''}`;
    }).join(' ' + conditionPanelData.matchType + ' ');
    setNodes(nds => nds.map(n => n.id === selectedNode.id ? { ...n, data: { ...n.data, label: summary, conditionData: conditionPanelData } } : n));
    setSelectedNode(null);
  };

  const handleActionDone = () => {
    const typeLabel = ACTION_TYPE_OPTS.find(o => o.value === actionPanelData.actionType)?.label || 'Action';
    const extra = actionPanelData.actionType === 'assign_group'
      ? actionPanelData.group
      : actionPanelData.actionType === 'assign_agent' ? actionPanelData.agentName
      : actionPanelData.actionType === 'change_priority' ? ACTION_PRIORITY_OPTS.find(o => o.value === actionPanelData.priority)?.label
      : actionPanelData.actionType === 'change_status' ? ACTION_STATUS_OPTS.find(o => o.value === actionPanelData.status)?.label
      : '';
    const label = extra ? `${typeLabel}: ${extra}` : typeLabel;
    setNodes(nds => nds.map(n => n.id === selectedNode.id ? { ...n, data: { ...n.data, label, actionData: actionPanelData } } : n));
    setSelectedNode(null);
  };

  const handleCreate = async () => {
    if (!createTitle.trim()) return;
    try {
      const created = await workflowsAPI.create({
        title: createTitle.trim(),
        description: createDesc.trim(),
        module: createModule,
        workflow_type: createType,
        status: 'draft',
      });
      const wf = {
        id: created.id,
        title: created.title || createTitle.trim(),
        description: created.description || createDesc.trim(),
        activatedBy: '',
        activatedOn: '',
        updatedBy: '',
        updatedOn: formatInTz(new Date(), { day: '2-digit', month: 'short', year: 'numeric' }),
        status: 'Draft',
        module: createModule,
        workflowType: createType,
        flow_data: null,
      };
      setWorkflows(wfs => [...wfs, wf]);
      setCreateOpen(false);
      setCreateTitle(''); setCreateDesc(''); setShowDesc(false);
      const { nodes: n, edges: e } = buildDemoFlow();
      setNodes(n); setEdges(e); setCanvasStatus('Draft');
      setEditingWf(wf);
    } catch (err) {
      showToast('Failed to create workflow: ' + err.message);
    }
  };

  if (editingWf) {
    return (
      <div style={{ position: 'absolute', inset: 0, display: 'flex', flexDirection: 'column', background: '#FFFFFF', zIndex: 20 }}>
        <div style={{ height: 52, display: 'flex', alignItems: 'center', gap: 10, padding: '0 20px', borderBottom: '1px solid #DDE1E7', flexShrink: 0, background: '#FFFFFF' }}>
          <span style={{ fontSize: 15, fontWeight: 700, color: '#1A202C' }}>{editingWf.title}</span>
          <span style={{ fontSize: 11, fontWeight: 700, color: '#718096', background: '#F0F2F5', borderRadius: 6, padding: '3px 10px' }}>{canvasStatus}</span>
          <button style={{ border: 'none', background: 'transparent', cursor: 'pointer', padding: 2 }}>
            <Icon name="edit" size={14} color="#718096" />
          </button>
          <div style={{ flex: 1 }} />
          <button onClick={async () => {
            try {
              await workflowsAPI.update(editingWf.id, { nodes, edges });
              setWorkflows(wfs => wfs.map(w => w.id === editingWf.id ? { ...w, _nodes: nodes, _edges: edges } : w));
              showToast('Workflow saved ✓');
            } catch (err) {
              showToast('Save failed: ' + err.message);
            }
          }} style={{ border: '1px solid #C8CDD6', borderRadius: 8, padding: '8px 18px', background: '#FFFFFF', color: '#2D3748', fontWeight: 600, fontSize: 13, cursor: 'pointer', marginRight: 8 }}>Save</button>
          <button onClick={() => setShowActivateConfirm(true)} style={{ border: 'none', borderRadius: 8, padding: '8px 22px', background: 'var(--accent)', color: '#fff', fontWeight: 700, fontSize: 13, cursor: 'pointer' }}>
            {canvasStatus === 'Active' ? '✓ Active' : 'Activate'}
          </button>
          <button style={{ border: 'none', background: 'transparent', cursor: 'pointer', fontSize: 20, color: '#718096', padding: '0 4px', lineHeight: 1 }}>&#8942;</button>
          <button onClick={() => setEditingWf(null)} style={{ border: 'none', background: 'transparent', cursor: 'pointer', fontSize: 22, color: '#718096', marginLeft: 4, lineHeight: 1 }}>&#xD7;</button>
        </div>

        <div style={{ display: 'flex', flex: 1, overflow: 'hidden' }}>
          <div style={{ width: 140, borderRight: '1px solid #DDE1E7', display: 'flex', flexDirection: 'column', padding: '12px 0', overflowY: 'auto', background: '#FFFFFF', flexShrink: 0 }}>
            {CANVAS_NODES.map(n => (
              <div key={n.type} draggable onDragStart={e => onDragStart(e, n.type)} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '10px 14px', cursor: 'grab', userSelect: 'none' }} title={`Drag to add ${n.label}`}>
                <div style={{ width: 30, height: 30, borderRadius: '50%', background: n.color, display: 'grid', placeItems: 'center', flexShrink: 0 }}>
                  <Icon name={n.icon} size={13} color="#fff" />
                </div>
                <span style={{ fontSize: 11, fontWeight: 700, color: '#2D3748', letterSpacing: '0.03em' }}>{n.label}</span>
              </div>
            ))}
          </div>

          <div style={{ flex: 1, position: 'relative' }} ref={reactFlowWrapper}>
            <ReactFlowProvider>
              <ReactFlow nodes={nodes} edges={edges} onNodesChange={onNodesChange} onEdgesChange={onEdgesChange} onConnect={onConnect} onInit={setRfInstance} onDrop={onDrop} onDragOver={onDragOver} nodeTypes={nodeTypes} fitView fitViewOptions={{ maxZoom: 1 }} style={{ background: '#FFFFFF' }}
                defaultEdgeOptions={EDGE_DEFAULTS}
                onNodeClick={(e, node) => openNodePanel(node)}>
                <Background variant={BackgroundVariant.Dots} gap={20} size={1} color="#C8CDD6" />
                <Controls />
              </ReactFlow>
            </ReactFlowProvider>

            {/* ── Event node click panel ── */}
            {selectedNode && selectedNode.type === 'Event' && (
              <div style={{ position: 'absolute', top: 0, right: 0, bottom: 0, width: 480, background: '#FFFFFF', borderLeft: '1px solid #DDE1E7', display: 'flex', flexDirection: 'column', zIndex: 50, boxShadow: '-4px 0 20px rgba(0,0,0,0.1)' }}>
                {/* Panel header */}
                <div style={{ padding: '14px 20px', borderBottom: '1px solid #DDE1E7', display: 'flex', alignItems: 'center', gap: 10, flexShrink: 0 }}>
                  <div style={{ width: 32, height: 32, borderRadius: '50%', background: '#3B5BDB', display: 'grid', placeItems: 'center', flexShrink: 0 }}>
                    <Icon name="check" size={14} color="#fff" />
                  </div>
                  <span style={{ fontSize: 16, fontWeight: 700, color: '#1A202C', flex: 1 }}>Event</span>
                  <button style={{ border: 'none', background: 'transparent', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 5, fontSize: 13, color: '#718096', fontWeight: 600, padding: '4px 8px', borderRadius: 6 }}>
                    <Icon name="copy" size={13} color="#718096" /> Clone
                  </button>
                  <button onClick={() => setSelectedNode(null)} style={{ border: 'none', background: 'transparent', cursor: 'pointer', fontSize: 22, color: '#718096', lineHeight: 1, marginLeft: 4 }}>&#xD7;</button>
                </div>

                {/* Panel body */}
                <div style={{ flex: 1, overflowY: 'auto', padding: '20px 24px' }}>
                  {/* Performed by */}
                  <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 22, flexWrap: 'wrap' }}>
                    <span style={{ fontSize: 13, color: '#2D3748', fontWeight: 500, whiteSpace: 'nowrap' }}>These events can be performed by</span>
                    <div style={{ position: 'relative', minWidth: 160 }}>
                      <select
                        value={eventPanelData.performedBy}
                        onChange={e => setEventPanelData(d => ({ ...d, performedBy: e.target.value }))}
                        style={{ width: '100%', border: '1px solid #C8CDD6', borderRadius: 8, padding: '7px 30px 7px 10px', fontSize: 13, color: '#1A202C', background: '#FFFFFF', appearance: 'none', outline: 'none', cursor: 'pointer' }}
                      >
                        {MEMBER_OPTS.map(m => <option key={m.value} value={m.value}>{m.label}</option>)}
                      </select>
                      <div style={{ position: 'absolute', right: 8, top: '50%', transform: 'translateY(-50%)', pointerEvents: 'none' }}>
                        <Icon name="chevronD" size={12} color="#718096" />
                      </div>
                    </div>
                  </div>

                  {/* When any events occur label */}
                  <div style={{ fontSize: 12, fontWeight: 700, color: '#718096', marginBottom: 12, textTransform: 'uppercase', letterSpacing: '0.05em' }}>When any of these events occur</div>

                  {/* Event rows */}
                  {eventPanelData.events.map((ev, i) => (
                    <React.Fragment key={i}>
                      {i > 0 && (
                        <div style={{ display: 'flex', alignItems: 'center', gap: 8, margin: '4px 0' }}>
                          <div style={{ flex: 1, height: 1, background: '#EEF1F5' }} />
                          <span style={{ fontSize: 10, fontWeight: 800, color: '#3B5BDB', background: '#EBF0FF', borderRadius: 99, padding: '2px 10px', letterSpacing: '0.08em' }}>OR</span>
                          <div style={{ flex: 1, height: 1, background: '#EEF1F5' }} />
                        </div>
                      )}
                      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4, background: '#F8FAFC', borderRadius: 10, padding: '10px 10px' }}>
                        {/* Event type dropdown */}
                        <div style={{ position: 'relative', flex: 1 }}>
                          <select
                            value={ev.type}
                            onChange={e => updateEventRow(i, 'type', e.target.value)}
                            style={{ width: '100%', border: '1px solid #C8CDD6', borderRadius: 8, padding: '7px 28px 7px 10px', fontSize: 12, color: '#1A202C', background: '#FFFFFF', appearance: 'none', outline: 'none', cursor: 'pointer' }}
                          >
                            {EVENT_TYPE_OPTS.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
                          </select>
                          <div style={{ position: 'absolute', right: 8, top: '50%', transform: 'translateY(-50%)', pointerEvents: 'none' }}>
                            <Icon name="chevronD" size={12} color="#718096" />
                          </div>
                        </div>

                        {/* Action dropdown */}
                        <div style={{ position: 'relative', flex: 1 }}>
                          <select
                            value={ev.action}
                            onChange={e => updateEventRow(i, 'action', e.target.value)}
                            style={{ width: '100%', border: '1px solid #C8CDD6', borderRadius: 8, padding: '7px 28px 7px 10px', fontSize: 12, color: '#1A202C', background: '#FFFFFF', appearance: 'none', outline: 'none', cursor: 'pointer' }}
                          >
                            {TICKET_ACTION_OPTS.map(a => <option key={a.value} value={a.value}>{a.label}</option>)}
                          </select>
                          <div style={{ position: 'absolute', right: 8, top: '50%', transform: 'translateY(-50%)', pointerEvents: 'none' }}>
                            <Icon name="chevronD" size={12} color="#718096" />
                          </div>
                        </div>

                        {/* Remove button */}
                        {eventPanelData.events.length > 1 && (
                          <button
                            onClick={() => removeEventRow(i)}
                            style={{ width: 26, height: 26, borderRadius: '50%', background: '#fee2e2', border: 'none', cursor: 'pointer', display: 'grid', placeItems: 'center', flexShrink: 0 }}
                          >
                            <span style={{ color: '#ef4444', fontWeight: 700, fontSize: 18, lineHeight: 1 }}>−</span>
                          </button>
                        )}
                      </div>
                    </React.Fragment>
                  ))}

                  {/* Add new event */}
                  <button
                    onClick={addEventRow}
                    style={{ width: '100%', marginTop: 10, border: '1.5px dashed #3B5BDB', borderRadius: 10, background: '#F5F8FF', color: '#3B5BDB', fontWeight: 700, fontSize: 13, cursor: 'pointer', padding: '10px 0', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6 }}
                  >
                    <span style={{ fontSize: 18, lineHeight: 1, fontWeight: 700 }}>+</span> Add another event (OR)
                  </button>

                  {/* Summary box */}
                  <div style={{ marginTop: 20, border: '1px solid #DDE1E7', borderRadius: 8, padding: '12px 14px', background: '#FFFFFF', fontSize: 13, color: '#2D3748', lineHeight: 1.7, minHeight: 44 }}>
                    {eventPanelData.events.map((ev, i) => {
                      const tLabel = EVENT_TYPE_OPTS.find(o => o.value === ev.type)?.label || 'Ticket is';
                      const aLabel = TICKET_ACTION_OPTS.find(o => o.value === ev.action)?.label || 'raised';
                      return <div key={i}>{tLabel} {aLabel}</div>;
                    })}
                  </div>
                </div>

                {/* Panel footer */}
                <div style={{ padding: '14px 24px', borderTop: '1px solid #DDE1E7', display: 'flex', justifyContent: 'flex-end', flexShrink: 0 }}>
                  <button
                    onClick={handleEventPanelDone}
                    style={{ border: 'none', borderRadius: 8, padding: '10px 32px', background: 'var(--accent)', color: '#fff', fontWeight: 700, fontSize: 13, cursor: 'pointer' }}
                  >
                    Done
                  </button>
                </div>
              </div>
            )}

            {/* ── Condition node click panel ── */}
            {selectedNode && selectedNode.type === 'Condition' && (
              <div style={{ position: 'absolute', top: 0, right: 0, bottom: 0, width: 480, background: '#FFFFFF', borderLeft: '1px solid #DDE1E7', display: 'flex', flexDirection: 'column', zIndex: 50, boxShadow: '-4px 0 20px rgba(0,0,0,0.1)' }}>
                <div style={{ padding: '14px 20px', borderBottom: '1px solid #DDE1E7', display: 'flex', alignItems: 'center', gap: 10, flexShrink: 0 }}>
                  <div style={{ width: 32, height: 32, borderRadius: '50%', background: '#f97316', display: 'grid', placeItems: 'center', flexShrink: 0 }}>
                    <Icon name="filter" size={14} color="#fff" />
                  </div>
                  <span style={{ fontSize: 16, fontWeight: 700, color: '#1A202C', flex: 1 }}>Condition</span>
                  <button onClick={() => setSelectedNode(null)} style={{ border: 'none', background: 'transparent', cursor: 'pointer', fontSize: 22, color: '#718096', lineHeight: 1 }}>&#xD7;</button>
                </div>
                <div style={{ flex: 1, overflowY: 'auto', padding: '20px 24px' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 18, fontSize: 13, color: '#2D3748', fontWeight: 500 }}>
                    Match
                    <div style={{ position: 'relative' }}>
                      <select value={conditionPanelData.matchType} onChange={e => setConditionPanelData(d => ({ ...d, matchType: e.target.value }))} style={{ border: '1px solid #3B5BDB', borderRadius: 8, padding: '5px 28px 5px 10px', fontSize: 13, color: '#3B5BDB', fontWeight: 700, background: '#EBF0FF', appearance: 'none', outline: 'none', cursor: 'pointer' }}>
                        <option>Any</option><option>All</option>
                      </select>
                      <div style={{ position: 'absolute', right: 8, top: '50%', transform: 'translateY(-50%)', pointerEvents: 'none' }}><Icon name="chevronD" size={12} color="#3B5BDB" /></div>
                    </div>
                    of the following conditions
                  </div>
                  {conditionPanelData.conditions.map((c, i) => {
                    const valueOpts = CONDITION_VALUE_OPTS[c.field];
                    const showValueInput = c.op !== 'is_empty' && c.op !== 'is_not_empty';
                    const matchLabel = conditionPanelData.matchType === 'Any' ? 'OR' : 'AND';
                    const matchColor = conditionPanelData.matchType === 'Any' ? '#3B5BDB' : '#059669';
                    const matchBg    = conditionPanelData.matchType === 'Any' ? '#EBF0FF' : '#ECFDF5';
                    return (
                      <React.Fragment key={i}>
                        {i > 0 && (
                          <div style={{ display: 'flex', alignItems: 'center', gap: 8, margin: '4px 0' }}>
                            <div style={{ flex: 1, height: 1, background: '#EEF1F5' }} />
                            <span style={{ fontSize: 10, fontWeight: 800, color: matchColor, background: matchBg, borderRadius: 99, padding: '2px 10px', letterSpacing: '0.08em' }}>{matchLabel}</span>
                            <div style={{ flex: 1, height: 1, background: '#EEF1F5' }} />
                          </div>
                        )}
                        <div style={{ background: '#F8FAFC', borderRadius: 10, padding: '10px', marginBottom: 4 }}>
                          <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' }}>
                            <div style={{ position: 'relative', flex: 2, minWidth: 120 }}>
                              <select value={c.field} onChange={e => setConditionPanelData(d => ({ ...d, conditions: d.conditions.map((r, idx) => idx === i ? { ...r, field: e.target.value, value: '' } : r) }))} style={{ width: '100%', border: '1px solid #C8CDD6', borderRadius: 8, padding: '7px 26px 7px 8px', fontSize: 12, color: '#1A202C', background: '#FFFFFF', appearance: 'none', outline: 'none', cursor: 'pointer' }}>
                                {CONDITION_FIELD_OPTS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
                              </select>
                              <div style={{ position: 'absolute', right: 6, top: '50%', transform: 'translateY(-50%)', pointerEvents: 'none' }}><Icon name="chevronD" size={11} color="#718096" /></div>
                            </div>
                            <div style={{ position: 'relative', flex: 1, minWidth: 100 }}>
                              <select value={c.op} onChange={e => setConditionPanelData(d => ({ ...d, conditions: d.conditions.map((r, idx) => idx === i ? { ...r, op: e.target.value } : r) }))} style={{ width: '100%', border: '1px solid #C8CDD6', borderRadius: 8, padding: '7px 26px 7px 8px', fontSize: 12, color: '#1A202C', background: '#FFFFFF', appearance: 'none', outline: 'none', cursor: 'pointer' }}>
                                {CONDITION_OP_OPTS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
                              </select>
                              <div style={{ position: 'absolute', right: 6, top: '50%', transform: 'translateY(-50%)', pointerEvents: 'none' }}><Icon name="chevronD" size={11} color="#718096" /></div>
                            </div>
                            {showValueInput && (
                              valueOpts ? (
                                <div style={{ position: 'relative', flex: 1, minWidth: 100 }}>
                                  <select value={c.value} onChange={e => setConditionPanelData(d => ({ ...d, conditions: d.conditions.map((r, idx) => idx === i ? { ...r, value: e.target.value } : r) }))} style={{ width: '100%', border: '1px solid #C8CDD6', borderRadius: 8, padding: '7px 26px 7px 8px', fontSize: 12, color: '#1A202C', background: '#FFFFFF', appearance: 'none', outline: 'none', cursor: 'pointer' }}>
                                    <option value="">Select…</option>
                                    {valueOpts.map(v => <option key={v} value={v}>{v}</option>)}
                                  </select>
                                  <div style={{ position: 'absolute', right: 6, top: '50%', transform: 'translateY(-50%)', pointerEvents: 'none' }}><Icon name="chevronD" size={11} color="#718096" /></div>
                                </div>
                              ) : (
                                <input value={c.value} onChange={e => setConditionPanelData(d => ({ ...d, conditions: d.conditions.map((r, idx) => idx === i ? { ...r, value: e.target.value } : r) }))} placeholder="Value" style={{ flex: 1, minWidth: 80, border: '1px solid #C8CDD6', borderRadius: 8, padding: '7px 8px', fontSize: 12, color: '#1A202C', outline: 'none' }} />
                              )
                            )}
                            {conditionPanelData.conditions.length > 1 && (
                              <button onClick={() => setConditionPanelData(d => ({ ...d, conditions: d.conditions.filter((_, idx) => idx !== i) }))} style={{ width: 26, height: 26, borderRadius: '50%', background: '#fee2e2', border: 'none', cursor: 'pointer', display: 'grid', placeItems: 'center', flexShrink: 0 }}>
                                <span style={{ color: '#ef4444', fontWeight: 700, fontSize: 18, lineHeight: 1 }}>−</span>
                              </button>
                            )}
                          </div>
                        </div>
                      </React.Fragment>
                    );
                  })}
                  <button onClick={() => setConditionPanelData(d => ({ ...d, conditions: [...d.conditions, { field: 'status', op: 'is', value: '' }] }))} style={{ width: '100%', marginTop: 10, border: '1.5px dashed #f97316', borderRadius: 10, background: '#FFF7ED', color: '#f97316', fontWeight: 700, fontSize: 13, cursor: 'pointer', padding: '10px 0', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6 }}>
                    <span style={{ fontSize: 18, lineHeight: 1, fontWeight: 700 }}>+</span> Add another condition ({conditionPanelData.matchType === 'Any' ? 'OR' : 'AND'})
                  </button>
                  <div style={{ marginTop: 20, border: '1px solid #DDE1E7', borderRadius: 8, padding: '12px 14px', background: '#FFFFFF', fontSize: 13, color: '#2D3748', lineHeight: 1.7, minHeight: 44 }}>
                    {conditionPanelData.conditions.map((c, i) => {
                      const f = CONDITION_FIELD_OPTS.find(o => o.value === c.field)?.label || c.field;
                      const op = CONDITION_OP_OPTS.find(o => o.value === c.op)?.label || c.op;
                      return <div key={i}>{f} {op}{c.value ? ' ' + c.value : ''}</div>;
                    })}
                  </div>
                </div>
                <div style={{ padding: '14px 24px', borderTop: '1px solid #DDE1E7', display: 'flex', justifyContent: 'flex-end', flexShrink: 0 }}>
                  <button onClick={handleConditionDone} style={{ border: 'none', borderRadius: 8, padding: '10px 32px', background: 'var(--accent)', color: '#fff', fontWeight: 700, fontSize: 13, cursor: 'pointer' }}>Done</button>
                </div>
              </div>
            )}

            {/* ── Action node click panel ── */}
            {selectedNode && selectedNode.type === 'Action' && (
              <div style={{ position: 'absolute', top: 0, right: 0, bottom: 0, width: 480, background: '#FFFFFF', borderLeft: '1px solid #DDE1E7', display: 'flex', flexDirection: 'column', zIndex: 50, boxShadow: '-4px 0 20px rgba(0,0,0,0.1)' }}>
                <div style={{ padding: '14px 20px', borderBottom: '1px solid #DDE1E7', display: 'flex', alignItems: 'center', gap: 10, flexShrink: 0 }}>
                  <div style={{ width: 32, height: 32, borderRadius: '50%', background: '#3b82f6', display: 'grid', placeItems: 'center', flexShrink: 0 }}>
                    <Icon name="promote" size={14} color="#fff" />
                  </div>
                  <span style={{ fontSize: 16, fontWeight: 700, color: '#1A202C', flex: 1 }}>Action</span>
                  <button onClick={() => setSelectedNode(null)} style={{ border: 'none', background: 'transparent', cursor: 'pointer', fontSize: 22, color: '#718096', lineHeight: 1 }}>&#xD7;</button>
                </div>
                <div style={{ flex: 1, overflowY: 'auto', padding: '20px 24px' }}>
                  <div style={{ marginBottom: 16 }}>
                    <label style={{ fontSize: 13, fontWeight: 600, color: '#2D3748', display: 'block', marginBottom: 6 }}>Action Type</label>
                    <div style={{ position: 'relative' }}>
                      <select value={actionPanelData.actionType} onChange={e => setActionPanelData(d => ({ ...d, actionType: e.target.value }))} style={{ width: '100%', border: '1px solid #C8CDD6', borderRadius: 8, padding: '9px 32px 9px 12px', fontSize: 13, color: '#1A202C', background: '#FFFFFF', appearance: 'none', outline: 'none', cursor: 'pointer' }}>
                        {ACTION_TYPE_OPTS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
                      </select>
                      <div style={{ position: 'absolute', right: 10, top: '50%', transform: 'translateY(-50%)', pointerEvents: 'none' }}><Icon name="chevronD" size={13} color="#718096" /></div>
                    </div>
                  </div>
                  {actionPanelData.actionType === 'assign_group' && (
                    <div style={{ marginBottom: 16 }}>
                      <label style={{ fontSize: 13, fontWeight: 600, color: '#2D3748', display: 'block', marginBottom: 6 }}>Group</label>
                      <div style={{ position: 'relative' }}>
                        <select value={actionPanelData.group} onChange={e => setActionPanelData(d => ({ ...d, group: e.target.value }))} style={{ width: '100%', border: '1px solid #C8CDD6', borderRadius: 8, padding: '9px 32px 9px 12px', fontSize: 13, color: '#1A202C', background: '#FFFFFF', appearance: 'none', outline: 'none', cursor: 'pointer' }}>
                          {workflowGroups.length === 0 && <option value="">Loading groups…</option>}
                          {workflowGroups.map(g => <option key={g.id} value={g.name}>{g.name}</option>)}
                        </select>
                        <div style={{ position: 'absolute', right: 10, top: '50%', transform: 'translateY(-50%)', pointerEvents: 'none' }}><Icon name="chevronD" size={13} color="#718096" /></div>
                      </div>
                    </div>
                  )}
                  {actionPanelData.actionType === 'assign_agent' && (
                    <div style={{ marginBottom: 16 }}>
                      <label style={{ fontSize: 13, fontWeight: 600, color: '#2D3748', display: 'block', marginBottom: 6 }}>Agent Name</label>
                      <input value={actionPanelData.agentName} onChange={e => setActionPanelData(d => ({ ...d, agentName: e.target.value }))} placeholder="Enter agent name" style={{ width: '100%', border: '1px solid #C8CDD6', borderRadius: 8, padding: '9px 12px', fontSize: 13, color: '#1A202C', outline: 'none', boxSizing: 'border-box' }} />
                    </div>
                  )}
                  {actionPanelData.actionType === 'add_note' && (
                    <div style={{ marginBottom: 16 }}>
                      <label style={{ fontSize: 13, fontWeight: 600, color: '#2D3748', display: 'block', marginBottom: 6 }}>Note</label>
                      <textarea value={actionPanelData.note} onChange={e => setActionPanelData(d => ({ ...d, note: e.target.value }))} rows={4} placeholder="Enter note content..." style={{ width: '100%', border: '1px solid #C8CDD6', borderRadius: 8, padding: '9px 12px', fontSize: 13, color: '#1A202C', outline: 'none', resize: 'vertical', boxSizing: 'border-box' }} />
                    </div>
                  )}
                  {actionPanelData.actionType === 'change_priority' && (
                    <div style={{ marginBottom: 16 }}>
                      <label style={{ fontSize: 13, fontWeight: 600, color: '#2D3748', display: 'block', marginBottom: 6 }}>Priority</label>
                      <div style={{ position: 'relative' }}>
                        <select value={actionPanelData.priority} onChange={e => setActionPanelData(d => ({ ...d, priority: e.target.value }))} style={{ width: '100%', border: '1px solid #C8CDD6', borderRadius: 8, padding: '9px 32px 9px 12px', fontSize: 13, color: '#1A202C', background: '#FFFFFF', appearance: 'none', outline: 'none', cursor: 'pointer' }}>
                          {ACTION_PRIORITY_OPTS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
                        </select>
                        <div style={{ position: 'absolute', right: 10, top: '50%', transform: 'translateY(-50%)', pointerEvents: 'none' }}><Icon name="chevronD" size={13} color="#718096" /></div>
                      </div>
                    </div>
                  )}
                  {actionPanelData.actionType === 'change_status' && (
                    <div style={{ marginBottom: 16 }}>
                      <label style={{ fontSize: 13, fontWeight: 600, color: '#2D3748', display: 'block', marginBottom: 6 }}>Status</label>
                      <div style={{ position: 'relative' }}>
                        <select value={actionPanelData.status} onChange={e => setActionPanelData(d => ({ ...d, status: e.target.value }))} style={{ width: '100%', border: '1px solid #C8CDD6', borderRadius: 8, padding: '9px 32px 9px 12px', fontSize: 13, color: '#1A202C', background: '#FFFFFF', appearance: 'none', outline: 'none', cursor: 'pointer' }}>
                          {ACTION_STATUS_OPTS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
                        </select>
                        <div style={{ position: 'absolute', right: 10, top: '50%', transform: 'translateY(-50%)', pointerEvents: 'none' }}><Icon name="chevronD" size={13} color="#718096" /></div>
                      </div>
                    </div>
                  )}
                  <div style={{ marginTop: 16, border: '1px solid #DDE1E7', borderRadius: 8, padding: '12px 14px', background: '#FFFFFF', fontSize: 13, color: '#2D3748', lineHeight: 1.7 }}>
                    {(() => {
                      const tl = ACTION_TYPE_OPTS.find(o => o.value === actionPanelData.actionType)?.label || '';
                      const ex = actionPanelData.actionType === 'assign_group' ? actionPanelData.group
                        : actionPanelData.actionType === 'assign_agent' ? actionPanelData.agentName
                        : actionPanelData.actionType === 'change_priority' ? ACTION_PRIORITY_OPTS.find(o => o.value === actionPanelData.priority)?.label
                        : actionPanelData.actionType === 'change_status' ? ACTION_STATUS_OPTS.find(o => o.value === actionPanelData.status)?.label
                        : actionPanelData.note || '';
                      return ex ? `${tl}: ${ex}` : tl;
                    })()}
                  </div>
                </div>
                <div style={{ padding: '14px 24px', borderTop: '1px solid #DDE1E7', display: 'flex', justifyContent: 'flex-end', flexShrink: 0 }}>
                  <button onClick={handleActionDone} style={{ border: 'none', borderRadius: 8, padding: '10px 32px', background: 'var(--accent)', color: '#fff', fontWeight: 700, fontSize: 13, cursor: 'pointer' }}>Done</button>
                </div>
              </div>
            )}
          </div>
        </div>

        {showActivateConfirm && (
          <div style={{ position: 'fixed', inset: 0, zIndex: 1000, background: 'rgba(0,0,0,0.35)', display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
            <div style={{ width: 400, background: '#FFFFFF', borderRadius: 16, padding: 28, boxShadow: '0 24px 60px rgba(0,0,0,0.15)' }}>
              <div style={{ fontSize: 17, fontWeight: 700, color: '#1A202C', marginBottom: 10 }}>Confirm Activation</div>
              <div style={{ fontSize: 14, color: '#718096', marginBottom: 24, lineHeight: 1.6 }}>You are trying to activate the automator. Please confirm to proceed?</div>
              <div style={{ display: 'flex', gap: 12, justifyContent: 'flex-end' }}>
                <button onClick={() => setShowActivateConfirm(false)} style={{ border: '1px solid #DDE1E7', borderRadius: 8, padding: '9px 18px', background: '#FFFFFF', cursor: 'pointer', fontWeight: 600, fontSize: 13 }}>Cancel</button>
                <button onClick={async () => {
                  setShowActivateConfirm(false);
                  try {
                    await workflowsAPI.update(editingWf.id, { nodes, edges, status: 'Active' });
                    setWorkflows(wfs => wfs.map(w => w.id === editingWf.id ? { ...w, status: 'Active', _nodes: nodes, _edges: edges } : w));
                    setCanvasStatus('Active');
                    showToast('Workflow activated — it will now run automatically!');
                  } catch (err) {
                    showToast('Failed to activate: ' + err.message);
                  }
                }} style={{ border: 'none', borderRadius: 8, padding: '9px 18px', background: 'var(--accent)', color: '#fff', cursor: 'pointer', fontWeight: 700, fontSize: 13 }}>Confirm</button>
              </div>
            </div>
          </div>
        )}

        {toast && (
          <div style={{ position: 'absolute', top: 60, left: 0, right: 0, zIndex: 200, display: 'flex', justifyContent: 'center', pointerEvents: 'none' }}>
            <div style={{ background: '#3B5BDB', color: '#fff', padding: '14px 28px', borderRadius: 10, fontWeight: 600, fontSize: 15, display: 'flex', alignItems: 'center', gap: 10, boxShadow: '0 8px 32px rgba(0,0,0,0.6)' }}>
              <Icon name="checkMark" size={16} color="#fff" /> {toast}
            </div>
          </div>
        )}
      </div>
    );
  }

  const filteredWfs = workflows.filter(wf => {
    const matchSearch = wf.title.toLowerCase().includes(searchWf.toLowerCase());
    const matchType   = activeSubItem === 'Scheduled Workflows' ? wf.workflowType === 'Scheduled Workflow' : wf.workflowType === 'Event Based Workflow';
    return matchSearch && matchType;
  });

  return (
    <div style={{ display: 'flex', height: '100%', background: '#FFFFFF', overflow: 'hidden', position: 'relative' }}>

      <div style={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
        <div style={{ padding: '14px 24px', borderBottom: '1px solid #DDE1E7', display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexShrink: 0 }}>
          <div style={{ fontSize: 15, fontWeight: 700, color: '#1A202C' }}>Tickets — {activeSubItem}</div>
          <div style={{ display: 'flex', gap: 8 }}>
            <button onClick={() => setShowTemplates(true)} style={{ border: '1px solid #C8CDD6', borderRadius: 8, padding: '8px 16px', background: '#FFFFFF', color: '#2D3748', fontWeight: 600, fontSize: 13, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 6 }}>
              ⚡ Templates
            </button>
            <button onClick={() => setCreateOpen(true)} style={{ border: 'none', borderRadius: 8, padding: '9px 20px', background: 'var(--accent)', color: '#fff', fontWeight: 700, fontSize: 13, cursor: 'pointer' }}>+ Create Workflow</button>
          </div>
        </div>

        {/* List / Log tabs */}
        <div style={{ display: 'flex', gap: 0, borderBottom: '2px solid #DDE1E7', padding: '0 24px', flexShrink: 0 }}>
          {[{ key: 'workflows', label: 'Workflows' }, { key: 'logs', label: '📋 Execution Log' }].map(t => (
            <button key={t.key} onClick={() => setWfListTab(t.key)} style={{ padding: '10px 16px', border: 'none', background: 'transparent', fontSize: 13, fontWeight: wfListTab === t.key ? 700 : 500, color: wfListTab === t.key ? '#3B5BDB' : '#718096', cursor: 'pointer', borderBottom: wfListTab === t.key ? '2px solid #3B5BDB' : '2px solid transparent', marginBottom: -2 }}>
              {t.label}
            </button>
          ))}
        </div>

        {wfListTab === 'workflows' && (<>
        <div style={{ padding: '12px 24px', flexShrink: 0, borderBottom: '1px solid #f3f4f6' }}>
          <div style={{ position: 'relative', maxWidth: 400 }}>
            <div style={{ position: 'absolute', left: 10, top: '50%', transform: 'translateY(-50%)', pointerEvents: 'none' }}>
              <Icon name="search" size={14} color="#718096" />
            </div>
            <input value={searchWf} onChange={e => setSearchWf(e.target.value)} placeholder="Search workflows using keywords" style={{ width: '100%', border: '1px solid #DDE1E7', borderRadius: 8, padding: '9px 12px 9px 32px', fontSize: 13, color: '#1A202C', outline: 'none', boxSizing: 'border-box' }} />
          </div>
        </div>

        <div style={{ flex: 1, overflowY: 'auto', padding: '0 24px 24px' }}>
          {filteredWfs.length === 0 ? (
            <div style={{ textAlign: 'center', color: '#718096', padding: 48, fontSize: 13 }}>
              No workflows found. Click <strong>⚡ Templates</strong> to get started quickly.
            </div>
          ) : (
            filteredWfs.map(wf => (
              <div key={wf.id} style={{ borderBottom: '1px solid #f3f4f6', padding: '16px 0', display: 'flex', alignItems: 'flex-start', gap: 12 }}>
                <div style={{ flex: 1 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4 }}>
                    <span onClick={() => openCanvas(wf)} style={{ fontSize: 14, fontWeight: 700, color: '#3B5BDB', cursor: 'pointer' }}>{wf.title}</span>
                    <span style={{ fontSize: 11, fontWeight: 700, background: wf.status === 'Active' ? '#E0E7FF' : '#F0F2F5', color: wf.status === 'Active' ? '#2C41A8' : '#718096', padding: '2px 8px', borderRadius: 99 }}>{wf.status}</span>
                  </div>
                  {wf.description && <div style={{ fontSize: 13, color: '#718096', marginBottom: 6, lineHeight: 1.5 }}>{wf.description}</div>}
                  <div style={{ fontSize: 12, color: '#718096', display: 'flex', gap: 16, flexWrap: 'wrap' }}>
                    {wf.activatedOn && <span><span style={{ color: '#2D3748', fontWeight: 500 }}>Activated:</span> {wf.activatedOn} by {wf.activatedBy}</span>}
                    <span><span style={{ color: '#2D3748', fontWeight: 500 }}>Updated:</span> {wf.updatedOn}</span>
                  </div>
                </div>
                <button onClick={() => openCanvas(wf)} style={{ border: '1px solid #DDE1E7', borderRadius: 7, padding: '6px 14px', background: '#FFFFFF', fontSize: 12, fontWeight: 600, color: '#2D3748', cursor: 'pointer', flexShrink: 0 }}>
                  <Icon name="edit" size={13} color="#718096" /> Edit
                </button>
              </div>
            ))
          )}
        </div>
        </>)}

        {wfListTab === 'logs' && (
          <div style={{ flex: 1, overflowY: 'auto', padding: '0 24px 24px' }}>
            {logsLoading ? (
              <div style={{ textAlign: 'center', padding: 48, color: '#718096', fontSize: 13 }}>Loading execution logs...</div>
            ) : logs.length === 0 ? (
              <div style={{ textAlign: 'center', padding: 48, color: '#718096' }}>
                <div style={{ fontSize: 14, fontWeight: 600, color: '#2D3748', marginBottom: 6 }}>No executions yet</div>
                <div style={{ fontSize: 13 }}>Activate a workflow and create/update a ticket to see logs here.</div>
              </div>
            ) : (
              <>
              <div style={{ display: 'flex', gap: 16, marginTop: 16, marginBottom: 8, padding: '8px 14px', background: '#FAFBFF', borderRadius: 8, border: '1px solid #E0E7FF', fontSize: 11, color: '#718096', alignItems: 'center', flexWrap: 'wrap' }}>
                <span style={{ fontWeight: 700, color: '#2D3748' }}>Legend:</span>
                <span style={{ color: '#16a34a', fontWeight: 700 }}>✓ Success</span>
                <span>= Workflow ran and actions executed</span>
                <span style={{ marginLeft: 8, fontWeight: 700, color: '#718096' }}>⏭ Skipped</span>
                <span>= Event type did not match the workflow trigger</span>
                <span style={{ marginLeft: 8, fontWeight: 700, color: '#dc2626' }}>✕ Error</span>
                <span>= Action failed</span>
              </div>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
                <thead>
                  <tr style={{ background: '#FFFFFF', position: 'sticky', top: 0, zIndex: 2 }}>
                    {['Time', 'Workflow', 'Ticket', 'Event', 'Actions Taken', 'Status'].map(h => (
                      <th key={h} style={{ padding: '10px 14px', textAlign: 'left', fontWeight: 700, color: '#2D3748', borderBottom: '1px solid #DDE1E7', whiteSpace: 'nowrap' }}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {logs.map(log => {
                    const actions = Array.isArray(log.actions_taken) ? log.actions_taken : (log.actions_taken ? JSON.parse(log.actions_taken) : []);
                    return (
                      <tr key={log.id} style={{ borderBottom: '1px solid #f3f4f6' }}>
                        <td style={{ padding: '10px 14px', color: '#718096', whiteSpace: 'nowrap', fontSize: 12 }}>
                          {formatInTz(log.executed_at, { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' })}
                        </td>
                        <td style={{ padding: '10px 14px', fontWeight: 600, color: '#1A202C' }}>{log.workflow_name}</td>
                        <td style={{ padding: '10px 14px', color: '#3B5BDB', fontWeight: 600 }}>{log.ticket_number || '—'}</td>
                        <td style={{ padding: '10px 14px', color: '#2D3748' }}>
                          <span style={{ background: '#eff6ff', color: '#2563eb', padding: '2px 8px', borderRadius: 5, fontSize: 11, fontWeight: 600 }}>
                            {log.event_type === 'ticket_created' ? 'Ticket Created' : log.event_type === 'ticket_updated' ? 'Ticket Updated' : log.event_type}
                          </span>
                        </td>
                        <td style={{ padding: '10px 14px', color: '#2D3748', maxWidth: 260 }}>
                          {actions.length === 0 ? <span style={{ color: '#718096' }}>—</span> : actions.map((a, i) => (
                            <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 5, marginBottom: 2 }}>
                              <span style={{ width: 6, height: 6, borderRadius: '50%', background: '#3B5BDB', display: 'inline-block', flexShrink: 0 }} />
                              <span style={{ fontSize: 12 }}>{a}</span>
                            </div>
                          ))}
                        </td>
                        <td style={{ padding: '10px 14px' }}>
                          {log.status === 'success' && (
                            <span style={{ fontSize: 11, fontWeight: 700, background: '#dcfce7', color: '#16a34a', padding: '3px 10px', borderRadius: 99, display: 'inline-flex', alignItems: 'center', gap: 4 }}>
                              ✓ Success
                            </span>
                          )}
                          {log.status === 'skipped' && (
                            <span title="Event type didn't match the workflow trigger condition" style={{ fontSize: 11, fontWeight: 700, background: '#F0F2F5', color: '#718096', padding: '3px 10px', borderRadius: 99, display: 'inline-flex', alignItems: 'center', gap: 4, cursor: 'help' }}>
                              ⏭ Skipped
                            </span>
                          )}
                          {log.status === 'error' && (
                            <span style={{ fontSize: 11, fontWeight: 700, background: '#fee2e2', color: '#dc2626', padding: '3px 10px', borderRadius: 99, display: 'inline-flex', alignItems: 'center', gap: 4 }}>
                              ✕ Error
                            </span>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
              </>
            )}
          </div>
        )}
      </div>

      {createOpen && (
        <div style={{ position: 'fixed', inset: 0, zIndex: 1000, background: 'rgba(0,0,0,0.3)', display: 'flex', justifyContent: 'flex-end' }}>
          <div style={{ width: 480, background: '#FFFFFF', height: '100%', display: 'flex', flexDirection: 'column', boxShadow: '-8px 0 32px rgba(0,0,0,0.12)' }}>
            <div style={{ padding: '24px 28px 16px', borderBottom: '1px solid #f3f4f6', display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', flexShrink: 0 }}>
              <div>
                <div style={{ fontSize: 20, fontWeight: 700, color: '#1A202C', marginBottom: 6 }}>Create Workflow</div>
                <div style={{ fontSize: 13, color: '#718096' }}>Choose the workflow type and module to create your workflow</div>
              </div>
              <button onClick={() => { setCreateOpen(false); setCreateTitle(''); setCreateDesc(''); setShowDesc(false); }} style={{ border: 'none', background: 'transparent', cursor: 'pointer', fontSize: 24, color: '#718096', lineHeight: 1, marginTop: -4 }}>&#xD7;</button>
            </div>

            <div style={{ flex: 1, overflowY: 'auto', padding: '24px 28px' }}>
              <div style={{ marginBottom: 20 }}>
                <label style={{ fontSize: 13, fontWeight: 600, color: '#2D3748', display: 'block', marginBottom: 6 }}>Title <span style={{ color: '#dc2626' }}>*</span></label>
                <input value={createTitle} onChange={e => setCreateTitle(e.target.value)} placeholder="example: Offboarding Date Automations" style={{ width: '100%', border: '1px solid #C8CDD6', borderRadius: 8, padding: '10px 14px', fontSize: 13, color: '#1A202C', outline: 'none', boxSizing: 'border-box' }} autoFocus />
              </div>

              {!showDesc ? (
                <button onClick={() => setShowDesc(true)} style={{ border: 'none', background: 'transparent', color: '#3B5BDB', fontWeight: 600, fontSize: 13, cursor: 'pointer', padding: 0, marginBottom: 20, display: 'block' }}>+ Add Description</button>
              ) : (
                <div style={{ marginBottom: 20 }}>
                  <label style={{ fontSize: 13, fontWeight: 600, color: '#2D3748', display: 'block', marginBottom: 6 }}>Description</label>
                  <textarea value={createDesc} onChange={e => setCreateDesc(e.target.value)} placeholder="Describe what this workflow does..." rows={3} style={{ width: '100%', border: '1px solid #C8CDD6', borderRadius: 8, padding: '10px 14px', fontSize: 13, color: '#1A202C', outline: 'none', resize: 'vertical', boxSizing: 'border-box' }} />
                </div>
              )}

              <div style={{ marginBottom: 20 }}>
                <label style={{ fontSize: 13, fontWeight: 600, color: '#2D3748', display: 'block', marginBottom: 6 }}>Module <span style={{ color: '#dc2626' }}>*</span></label>
                <div style={{ position: 'relative' }}>
                  <select value={createModule} onChange={e => setCreateModule(e.target.value)} style={{ width: '100%', border: '1px solid #C8CDD6', borderRadius: 8, padding: '10px 14px', fontSize: 13, color: '#1A202C', background: '#FFFFFF', appearance: 'none', outline: 'none', boxSizing: 'border-box', cursor: 'pointer' }}>
                    {MODULE_OPTS.map(m => <option key={m}>{m}</option>)}
                  </select>
                  <div style={{ position: 'absolute', right: 12, top: '50%', transform: 'translateY(-50%)', pointerEvents: 'none' }}><Icon name="chevronD" size={14} color="#718096" /></div>
                </div>
              </div>

              <div style={{ marginBottom: 20 }}>
                <label style={{ fontSize: 13, fontWeight: 600, color: '#2D3748', display: 'block', marginBottom: 6 }}>Workflow Type <span style={{ color: '#dc2626' }}>*</span></label>
                <div style={{ position: 'relative' }}>
                  <select value={createType} onChange={e => setCreateType(e.target.value)} style={{ width: '100%', border: '1px solid #C8CDD6', borderRadius: 8, padding: '10px 14px', fontSize: 13, color: '#1A202C', background: '#FFFFFF', appearance: 'none', outline: 'none', boxSizing: 'border-box', cursor: 'pointer' }}>
                    {TYPE_OPTS.map(t => <option key={t}>{t}</option>)}
                  </select>
                  <div style={{ position: 'absolute', right: 12, top: '50%', transform: 'translateY(-50%)', pointerEvents: 'none' }}><Icon name="chevronD" size={14} color="#718096" /></div>
                </div>
              </div>
            </div>

            <div style={{ padding: '16px 28px', borderTop: '1px solid #f3f4f6', display: 'flex', gap: 12, flexShrink: 0 }}>
              <button onClick={() => { setCreateOpen(false); setCreateTitle(''); setCreateDesc(''); setShowDesc(false); }} style={{ flex: 1, border: '1px solid #C8CDD6', borderRadius: 8, padding: '10px', background: '#FFFFFF', cursor: 'pointer', fontWeight: 600, fontSize: 13, color: '#2D3748' }}>Cancel</button>
              <button onClick={handleCreate} disabled={!createTitle.trim()} style={{ flex: 1, border: 'none', borderRadius: 8, padding: '10px', background: createTitle.trim() ? 'var(--accent)' : 'var(--accent)', color: '#fff', cursor: createTitle.trim() ? 'pointer' : 'not-allowed', fontWeight: 700, fontSize: 13 }}>Create</button>
            </div>
          </div>
        </div>
      )}

      {/* ── Templates modal ── */}
      {showTemplates && (
        <div style={{ position: 'fixed', inset: 0, zIndex: 1000, background: 'rgba(0,0,0,0.35)', display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
          <div style={{ width: 680, background: '#FFFFFF', borderRadius: 16, padding: 28, boxShadow: '0 24px 60px rgba(0,0,0,0.18)', maxHeight: '80vh', overflowY: 'auto' }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 20 }}>
              <div>
                <div style={{ fontSize: 18, fontWeight: 700, color: '#1A202C' }}>⚡ Workflow Templates</div>
                <div style={{ fontSize: 13, color: '#718096', marginTop: 4 }}>Pick a pre-built template to get started instantly</div>
              </div>
              <button onClick={() => setShowTemplates(false)} style={{ border: 'none', background: 'transparent', cursor: 'pointer', fontSize: 24, color: '#718096', lineHeight: 1 }}>&#xD7;</button>
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
              {TEMPLATES.map((tmpl, i) => {
                const isAutoAssign = i === 0;
                return (
                  <div key={i} style={{ border: isAutoAssign ? '2px solid #3B5BDB' : '1px solid #DDE1E7', borderRadius: 12, padding: '18px 20px', display: 'flex', alignItems: 'center', gap: 16, transition: 'border 0.15s, box-shadow 0.15s', background: isAutoAssign ? '#f5f7ff' : '#fff' }}>
                    <div style={{ width: 44, height: 44, borderRadius: 10, background: isAutoAssign ? '#3B5BDB' : '#E0E7FF', display: 'grid', placeItems: 'center', fontSize: 20, flexShrink: 0 }}>
                      {['⚡', '🔄', '📧', '⭐'][i] || '⚡'}
                    </div>
                    <div style={{ flex: 1 }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4 }}>
                        <div style={{ fontSize: 14, fontWeight: 700, color: '#1A202C' }}>{tmpl.name}</div>
                        {isAutoAssign && <span style={{ fontSize: 10, fontWeight: 800, background: '#16a34a', color: '#fff', padding: '2px 7px', borderRadius: 99 }}>RECOMMENDED</span>}
                      </div>
                      <div style={{ fontSize: 13, color: '#718096', lineHeight: 1.5 }}>{tmpl.description}</div>
                      <div style={{ display: 'flex', gap: 6, marginTop: 8 }}>
                        {tmpl.nodes.filter(n => n.type === 'Event').map((_, j) => <span key={j} style={{ fontSize: 11, background: '#E0E7FF', color: '#2C41A8', padding: '2px 8px', borderRadius: 5, fontWeight: 600 }}>Event</span>)}
                        {tmpl.nodes.filter(n => n.type === 'Condition').map((_, j) => <span key={j} style={{ fontSize: 11, background: '#ffedd5', color: '#c2410c', padding: '2px 8px', borderRadius: 5, fontWeight: 600 }}>Condition</span>)}
                        {tmpl.nodes.filter(n => n.type === 'Action').map((_, j) => <span key={j} style={{ fontSize: 11, background: '#dbeafe', color: '#1d4ed8', padding: '2px 8px', borderRadius: 5, fontWeight: 600 }}>Action</span>)}
                      </div>
                    </div>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 8, flexShrink: 0 }}>
                      {isAutoAssign && (
                        <button onClick={() => deployTemplate(tmpl)} style={{ border: 'none', borderRadius: 8, padding: '9px 20px', background: '#16a34a', color: '#fff', fontWeight: 800, fontSize: 13, cursor: 'pointer', whiteSpace: 'nowrap' }}>
                          ✅ Deploy & Activate
                        </button>
                      )}
                      <button onClick={() => loadTemplateWorkflow(tmpl)} style={{ border: '1px solid #C8CDD6', borderRadius: 8, padding: '8px 20px', background: '#fff', color: '#2D3748', fontWeight: 700, fontSize: 13, cursor: 'pointer', whiteSpace: 'nowrap' }}>
                        Edit First
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      )}

      {toast && (
        <div style={{ position: 'absolute', top: 60, left: 0, right: 0, zIndex: 200, display: 'flex', justifyContent: 'center', pointerEvents: 'none' }}>
          <div style={{ background: '#3B5BDB', color: '#fff', padding: '14px 28px', borderRadius: 10, fontWeight: 600, fontSize: 15, display: 'flex', alignItems: 'center', gap: 10, boxShadow: '0 8px 32px rgba(0,0,0,0.6)' }}>
            <Icon name="checkMark" size={16} color="#fff" /> {toast}
          </div>
        </div>
      )}
    </div>
  );
}